// <!-- GRACE:MODULE_START name="ui_auth" -->
// <!-- GRACE:DEPENDENCY target="ui_auth" -->
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, Link } from 'react-router-dom';
import { resetPassword } from '../../services/authService';

interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

// <!-- GRACE:CONTRACT function="ResetPasswordPage" -->
// <LLM_NOTE>
// Открывается по ссылке из письма: /reset-password/:token.
// Токен одноразовый и живёт PASSWORD_RESET_EXPIRATION_MINUTES (backend, default 60 мин).
// </LLM_NOTE>
export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm<ResetPasswordForm>();
  const newPassword = watch('newPassword');

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) return;
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const res = await resetPassword(token, data.newPassword);
      setMessage(res.message);
    } catch (e: any) {
      setError(e.message || 'Ошибка смены пароля');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-2xl border bg-white p-8 shadow-sm">
        <div className="mb-6">
          <h1 className="text-xl font-semibold">Новый пароль</h1>
        </div>

        {message ? (
          <>
            <div className="mb-6 rounded-lg bg-green-50 p-3 text-sm text-green-700">{message}</div>
            <Link
              to="/login"
              className="inline-block rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              Перейти к входу
            </Link>
          </>
        ) : (
          <>
            {error && (
              <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">
                {error}{' '}
                <Link to="/forgot-password" className="font-medium underline">
                  Запросить новую ссылку
                </Link>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">Новый пароль</label>
                <input
                  type="password"
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:border-gray-400 focus:outline-none"
                  placeholder="Минимум 8 символов"
                  {...register('newPassword', {
                    required: 'Введите новый пароль',
                    minLength: { value: 8, message: 'Минимум 8 символов' },
                  })}
                />
                {errors.newPassword && (
                  <p className="mt-1 text-xs text-red-500">{errors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Подтверждение пароля</label>
                <input
                  type="password"
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:border-gray-400 focus:outline-none"
                  placeholder="Повторите новый пароль"
                  {...register('confirmPassword', {
                    required: 'Подтвердите новый пароль',
                    validate: (v) => v === newPassword || 'Пароли не совпадают',
                  })}
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-500">{errors.confirmPassword.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !token}
                className="w-full rounded-lg bg-gray-900 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : 'Сохранить пароль'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
// <!-- GRACE:MODULE_END name="ui_auth" -->
